import React, { useState, useRef, useEffect } from 'react';
import {
  MapPin,
  MapPinned,
  MailPlus,
  KanbanSquare,
  Palette,
  Settings,
  Sparkles,
  Layers3,
  Sofa,
  PenTool,
  Globe,
  Film,
  Video,
  Monitor,
  Wrench,
  MoonStar,
  LayoutDashboard,
  LayoutGrid,
  ChevronDown,
  LogOut,
  LogIn,
  UserPlus,
  Crown,
  CreditCard,
  Scissors,
  Clapperboard,
  SlidersHorizontal,
  Receipt,
  Loader2,
  CircleDot,
  FileText,
  FilePen,
} from 'lucide-react';
import { supabase, openBillingPortal } from '../services/supabase';
import { fetchSubscription, getDoorstepsTier, type DoorstepsTier } from '../services/pricing';
import type { AppView } from '../types';

const HyperDuskIcon = ({ size = 18, className }: { size?: number; className?: string }) => (
  <MoonStar size={size} className={className} strokeWidth={1.8} />
);

// Apps grouped the way the Hub buckets them, so the switcher gives context.
// Each group carries an accent colour + gradient used to theme its section.
const APP_GROUPS = [
  {
    id: 'workspace', label: 'Workspace',
    accent: 'text-purple-300', iconBg: 'bg-purple-500/15 text-purple-300', gradient: 'from-purple-500/20',
    apps: [
      { id: 'hub', label: 'Hub', icon: LayoutDashboard, url: 'https://hub.rehub.studio' },
      { id: 'studio', label: 'Studio', icon: Clapperboard, url: 'https://studio.rehub.studio' },
      { id: 'doorsteps', label: 'Doorsteps', icon: MapPin, url: 'https://doorsteps.rehub.studio' },
    ],
  },
  {
    id: 'photo', label: 'AI Photo',
    accent: 'text-sky-300', iconBg: 'bg-sky-500/15 text-sky-300', gradient: 'from-sky-500/20',
    apps: [
      { id: 'photoclear', label: 'Photoclear', icon: Sparkles, url: 'https://photoclear.rehub.studio' },
      { id: 'hdrphoto', label: 'HDR Photo', icon: Layers3, url: 'https://hdrphoto.rehub.studio' },
      { id: 'photoedit', label: 'PhotoEdit', icon: SlidersHorizontal, url: 'https://photoedit.rehub.studio' },
      { id: 'staging', label: 'Staging', icon: Sofa, url: 'https://staging.rehub.studio' },
    ],
  },
  {
    id: 'video', label: 'AI Video',
    accent: 'text-fuchsia-300', iconBg: 'bg-fuchsia-500/15 text-fuchsia-300', gradient: 'from-fuchsia-500/20',
    apps: [
      { id: 'imagemotion', label: 'Imagemotion', icon: Film, url: 'https://imagemotion.rehub.studio' },
      { id: 'videomotion', label: 'Videomotion', icon: Video, url: 'https://videomotion.rehub.studio' },
      { id: 'propertycut', label: 'PropertyCuts', icon: Scissors, url: 'https://propertycuts.rehub.studio' },
      { id: 'hyperdusk', label: 'Hyper Dusk', icon: HyperDuskIcon, url: 'https://hyperdusk.rehub.studio' },
      { id: 'record', label: 'Screen Record', icon: CircleDot, url: 'https://record.rehub.studio' },
    ],
  },
  {
    id: 'tours', label: 'Tours & Floor Plans',
    accent: 'text-emerald-300', iconBg: 'bg-emerald-500/15 text-emerald-300', gradient: 'from-emerald-500/20',
    apps: [
      { id: 'tours', label: '360 Tours', icon: Globe, url: 'https://360tours.rehub.studio' },
      { id: 'planos', label: 'Planos', icon: MapPinned, url: 'https://planos.rehub.studio' },
      { id: 'sketchplan', label: 'Sketchplan', icon: PenTool, url: 'https://sketchplan.rehub.studio' },
    ],
  },
  {
    id: 'present', label: 'Presentations',
    accent: 'text-amber-300', iconBg: 'bg-amber-500/15 text-amber-300', gradient: 'from-amber-500/20',
    apps: [
      { id: 'showcase', label: 'Showcase', icon: Monitor, url: 'https://showcase.rehub.studio' },
      { id: 'brochures', label: 'BrochurePDF', icon: FileText, url: 'https://brochures.rehub.studio' },
      { id: 'pdf', label: 'ReHub PDF', icon: FilePen, url: 'https://pdf.rehub.studio' },
    ],
  },
  {
    id: 'tools', label: 'Free Tools',
    accent: 'text-slate-300', iconBg: 'bg-slate-500/15 text-slate-300', gradient: 'from-slate-500/20',
    apps: [
      { id: 'tools', label: 'Tools', icon: Wrench, url: 'https://tools.rehub.studio' },
    ],
  },
] as const;

const APPS = APP_GROUPS.flatMap((g) => g.apps as readonly (typeof APP_GROUPS)[number]['apps'][number][]);
type AppId = typeof APP_GROUPS[number]['apps'][number]['id'];
const CURRENT_APP: AppId = 'doorsteps';

const handleAppNavigation = async (
  targetApp: AppId,
  url: string,
  e: React.MouseEvent
) => {
  if (targetApp === CURRENT_APP) {
    e.preventDefault();
    return;
  }
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      e.preventDefault();
      window.location.href = `${url}#sso_access_token=${session.access_token}&sso_refresh_token=${session.refresh_token}`;
    }
  } catch {
    // Fall through to default <a> navigation
  }
};

const ProductSwitcher: React.FC = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  return (
    <div className="relative hidden md:block" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800/90 border border-slate-600/50 text-slate-200 hover:text-white hover:bg-slate-700/80 transition-all text-sm font-medium"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <LayoutGrid size={16} />
        <span>Apps</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-[340px] max-h-[80vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl z-50">
          <div className="relative overflow-hidden rounded-2xl">
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_0%_0%,rgba(168,85,247,0.18),transparent_55%),radial-gradient(120%_80%_at_100%_100%,rgba(56,189,248,0.12),transparent_55%)]" />
            <div className="relative p-3 space-y-3">
              {APP_GROUPS.map((group) => (
                <div key={group.id}>
                  <div className={`px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider ${group.accent}`}>{group.label}</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {group.apps.map((app) => (
                      <a
                        key={app.id}
                        href={app.url}
                        onClick={(e) => { handleAppNavigation(app.id, app.url, e); setOpen(false); }}
                        className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                          CURRENT_APP === app.id
                            ? `bg-gradient-to-br ${group.gradient} to-transparent text-white ring-1 ring-inset ring-white/15`
                            : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
                        }`}
                      >
                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${group.iconBg}`}>
                          <app.icon size={16} strokeWidth={1.8} />
                        </span>
                        <span className="truncate">{app.label}</span>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MobileAppSwitcher: React.FC = () => (
  <div className="flex items-center gap-1.5" aria-label="Switch app">
    {APPS.map((app) => (
      <a
        key={app.id}
        href={app.url}
        onClick={(e) => handleAppNavigation(app.id, app.url, e)}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all touch-manipulation ${
          CURRENT_APP === app.id
            ? 'bg-emerald-600 border-emerald-500 text-white'
            : 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300 active:bg-slate-800'
        }`}
        aria-label={`Switch to ${app.label}`}
      >
        <app.icon size={18} strokeWidth={2} />
      </a>
    ))}
  </div>
);

const TABS: Array<{ view: AppView; label: string; icon: React.ReactNode }> = [
  { view: 'PROSPECT', label: 'Prospect', icon: <MapPin size={14} /> },
  { view: 'CAMPAIGNS', label: 'Campaigns', icon: <MailPlus size={14} /> },
  { view: 'PIPELINE', label: 'Pipeline', icon: <KanbanSquare size={14} /> },
  { view: 'DESIGN', label: 'Design', icon: <Palette size={14} /> },
];

interface HeaderProps {
  user: { id?: string; email?: string | null } | null;
  view?: AppView;
  onNavigate?: (view: AppView) => void;
  onSignOut?: () => void;
  onLogin?: () => void;
  onSignUp?: () => void;
}

const TIER_BADGE_STYLES: Record<DoorstepsTier, string> = {
  free: 'bg-slate-800 text-slate-300 border-slate-700',
  starter: 'bg-sky-900/40 text-sky-300 border-sky-700/40',
  pro: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40',
  agency: 'bg-amber-900/40 text-amber-300 border-amber-700/40',
};

export default function Header({
  user,
  view,
  onNavigate,
  onSignOut,
  onLogin,
  onSignUp,
}: HeaderProps) {
  const [accountOpen, setAccountOpen] = useState(false);
  const [tier, setTier] = useState<DoorstepsTier>('free');
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);
  const accountRef = useRef<HTMLDivElement>(null);

  const handleBillingPortal = async () => {
    if (billingLoading) return;
    setBillingNotice(null);
    setBillingLoading(true);
    try {
      const result = await openBillingPortal();
      if ('noCustomer' in result) {
        setBillingNotice("No invoices yet — buy credits or a plan and they'll appear here.");
      } else {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('[Billing] portal failed:', err);
      setBillingNotice("Couldn't open billing right now. Please try again.");
    } finally {
      setBillingLoading(false);
    }
  };

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchSubscription(user.id).then((s) => setTier(s.tier));
    }
  }, [user?.id]);

  const tierMeta = getDoorstepsTier(tier);

  return (
    <header className="sticky top-0 left-0 right-0 bg-black border-b border-slate-900 shrink-0 z-40 flex flex-col px-4 sm:px-6">
      {/* Row 1: brand + app switcher + account */}
      <div className="flex items-center min-h-14 gap-3">
        <button
          className="flex items-center shrink-0 group"
          onClick={() => onNavigate?.('PROSPECT')}
          aria-label="Doorsteps home"
        >
          <img
            src="/Logo.png"
            alt="Doorsteps"
            className="h-3 sm:h-3.5 w-auto group-hover:opacity-90 transition-opacity"
          />
        </button>

        <ProductSwitcher />

        {/* Authenticated tab nav, inline on desktop */}
        {user && view && onNavigate && (
          <nav className="hidden lg:flex items-center gap-1 ml-4 bg-slate-900/60 border border-slate-800 p-1 rounded-xl">
            {TABS.map((t) => {
              const active =
                view === t.view ||
                (t.view === 'CAMPAIGNS' && view === 'CAMPAIGN_DETAIL');
              return (
                <button
                  key={t.view}
                  onClick={() => onNavigate(t.view)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-500/30'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {t.icon}
                  {t.label}
                </button>
              );
            })}
          </nav>
        )}

        <div className="flex items-center gap-2 ml-auto" ref={accountRef}>
          {user ? (
            <>
              <button
                onClick={() => onNavigate?.('PRICING')}
                title={`You're on Doorsteps ${tierMeta.name}`}
                className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors ${TIER_BADGE_STYLES[tier]} ${tier === 'free' ? 'hover:border-emerald-500/50 hover:text-emerald-300' : ''}`}
              >
                {tier !== 'free' && <Crown size={11} />}
                {tierMeta.name}
                {tier === 'free' && <span className="ml-1 opacity-60">· Upgrade</span>}
              </button>
              <button
                onClick={() => onNavigate?.('SETTINGS')}
                className={`p-2 rounded-xl transition-colors ${
                  view === 'SETTINGS'
                    ? 'bg-emerald-600/20 text-emerald-300'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                aria-label="Settings"
              >
                <Settings size={18} />
              </button>
              <div className="relative">
                <button
                  onClick={() => setAccountOpen((o) => !o)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium min-h-[40px]"
                  aria-expanded={accountOpen}
                  aria-haspopup="true"
                >
                  <span className="max-w-[140px] truncate hidden sm:inline">
                    {user.email || 'Account'}
                  </span>
                  <ChevronDown size={14} className={`transition-transform ${accountOpen ? 'rotate-180' : ''}`} />
                </button>
                {accountOpen && (
                  <div className="absolute right-0 top-full mt-1 py-1 min-w-[200px] bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50">
                    <button
                      onClick={() => { setAccountOpen(false); onNavigate?.('PRICING'); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                    >
                      <CreditCard size={14} /> Pricing & billing
                    </button>
                    <button
                      onClick={() => { setAccountOpen(false); onNavigate?.('SETTINGS'); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                    >
                      <Settings size={14} /> Settings
                    </button>
                    <button
                      onClick={handleBillingPortal}
                      disabled={billingLoading}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm disabled:opacity-60"
                    >
                      {billingLoading ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} />}
                      Billing &amp; invoices
                    </button>
                    {billingNotice && (
                      <div className="px-4 py-2 text-xs text-amber-300/90">{billingNotice}</div>
                    )}
                    <div className="border-t border-slate-800 my-1"></div>
                    <button
                      onClick={() => { setAccountOpen(false); onSignOut?.(); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                    >
                      <LogOut size={14} /> Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-1.5 sm:gap-3">
              {onLogin && (
                <button
                  onClick={onLogin}
                  className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2.5 min-h-[44px] text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl font-medium transition-all text-sm"
                >
                  <LogIn size={18} />
                  <span className="hidden sm:inline">Log In</span>
                </button>
              )}
              {onSignUp && (
                <button
                  onClick={onSignUp}
                  className="flex items-center justify-center gap-1.5 px-4 sm:px-5 py-2.5 min-h-[44px] bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold transition-all shadow-lg shadow-emerald-500/30 text-sm"
                >
                  <UserPlus size={18} />
                  <span>Sign Up</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Row 2 (mobile / md): tabs + app switcher carousel */}
      {user && view && onNavigate && (
        <div className="lg:hidden border-t border-slate-900 py-2 flex items-center gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const active =
              view === t.view ||
              (t.view === 'CAMPAIGNS' && view === 'CAMPAIGN_DETAIL');
            return (
              <button
                key={t.view}
                onClick={() => onNavigate(t.view)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0 ${
                  active
                    ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-500/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {t.icon}
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="md:hidden border-t border-slate-900 py-2.5 flex items-center justify-center gap-3 overflow-x-auto shrink-0">
        <MobileAppSwitcher />
      </div>
    </header>
  );
}
